import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaDownload } from "react-icons/fa";
import api from "../../services/api";
import { groupPeriodsByUser, resolveMonthlyCost } from "../../utils/user_contract";
import Loader from "../../components/loader";
import toast from "react-hot-toast";

const FULL_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TIME_OFF_PROJECTS = ["unavailable", "leave paid", "rtt", "leave unpaid", "arret maladie", "holiday", "holliday", "congé sans solde", "timeoff"];

function getWorkingDaysInMonth(year, month) {
  const dim = new Date(year, month + 1, 0).getDate();
  let c = 0;
  for (let d = 1; d <= dim; d++) {
    const day = new Date(year, month, d).getDay();
    if (day !== 0 && day !== 6) c++;
  }
  return c;
}

function getElapsedWorkingDays(year, month) {
  const today = new Date();
  if (today.getFullYear() !== year || today.getMonth() !== month) {
    if (new Date(year, month, 1) < today) return getWorkingDaysInMonth(year, month);
    return 0;
  }
  let c = 0;
  for (let d = 1; d <= today.getDate(); d++) {
    const day = new Date(year, month, d).getDay();
    if (day !== 0 && day !== 6) c++;
  }
  return c;
}

// === Color helpers (inspired by SalesOps TACE colors) ===
const getMarginColor = (marginPct) => {
  if (marginPct >= 40) return "bg-green-500 text-white";
  if (marginPct >= 30) return "bg-green-400 text-white";
  if (marginPct >= 20) return "bg-green-300";
  if (marginPct >= 10) return "bg-yellow-300";
  if (marginPct >= 0) return "bg-orange-300";
  return "bg-red-400 text-white";
};

const getMarginTextColor = (marginPct) => {
  if (marginPct >= 30) return "text-green-600";
  if (marginPct >= 15) return "text-yellow-600";
  return "text-red-600";
};

const getDeltaColor = (v) => {
  if (v > 0) return "text-green-600 font-medium";
  if (v < 0) return "text-red-600 font-medium";
  return "text-gray-400";
};

const fmt = (n) => {
  if (n === undefined || n === null || isNaN(n)) return "—";
  const rounded = Math.round(n);
  if (Math.abs(rounded) >= 1000) return `${(rounded / 1000).toFixed(1)}k`;
  return rounded.toLocaleString("en-US");
};

const fmtFull = (n) => {
  if (n === undefined || n === null || isNaN(n)) return "—";
  return Math.round(n).toLocaleString("en-US");
};

export default function Forecast({ user, filters: parentFilters, organisation, businessUnit }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isSeparateBU, setIsSeparateBU] = useState(false); // true for Tirana/Walego (have own bank accounts)
  const [hoveredCell, setHoveredCell] = useState({ row: null, col: null });
  const [expandedPersons, setExpandedPersons] = useState(new Set());
  const [searchFilter, setSearchFilter] = useState("");
  const [sortKey, setSortKey] = useState("margin"); // margin, revenue, cost, name

  // Generate 6 months: 2 past + current + 3 future
  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 6 }, (_, i) => {
      const offset = i - 2; // -2, -1, 0, 1, 2, 3
      const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const workingDays = getWorkingDaysInMonth(d.getFullYear(), d.getMonth());
      const elapsed = getElapsedWorkingDays(d.getFullYear(), d.getMonth());
      return {
        year: d.getFullYear(),
        month: d.getMonth(),
        label: `${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`,
        fullLabel: `${FULL_MONTHS[d.getMonth()]} ${d.getFullYear()}`,
        shortLabel: SHORT_MONTHS[d.getMonth()],
        workingDays,
        elapsed,
        progressPct: workingDays > 0 ? (elapsed / workingDays) * 100 : 0,
        isPast: offset < 0,
        isCurrent: offset === 0,
        isFuture: offset > 0,
      };
    });
  }, []);

  const openUserActivity = (userId) => {
    const params = new URLSearchParams(location.search);
    params.set("user_modal_id", userId);
    params.set("user_modal_tab", "activity");
    navigate({ pathname: location.pathname, search: params.toString() });
  };

  useEffect(() => {
    fetchData();
  }, [user, parentFilters, businessUnit]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const today = new Date();

      // Fetch team members
      const userQuery = {
        role: ["admin", "user", "super_admin"],
        contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "FREELANCE", "Me kohe te plote"],
      };
      if (user) {
        userQuery.manager_id = user._id;
        userQuery.managees_of_managees = parentFilters?.manageesOfManagees || false;
      }
      if (businessUnit._id !== "ALL") {
        userQuery.business_unit_id = businessUnit._id;
      } else if (organisation?._id && organisation._id !== "ALL") {
        userQuery.organisation_id = organisation._id;
      }

      const [{ data: activeData }, { data: inactiveData }] = await Promise.all([
        api.post("/user/search", { ...userQuery, status: "active" }),
        api.post("/user/search", { ...userQuery, status: "inactive" }),
      ]);

      let allMembers = [...(activeData?.users || []), ...(inactiveData?.users || [])];

      // Add the manager themselves if not already in the list (same as ProfitabilityCard)
      if (user && !allMembers.some((m) => m._id === user._id)) {
        allMembers.push(user);
      }

      allMembers = allMembers.filter((u) => {
        if (u.starting_date && new Date(u.starting_date) > today) return false;
        return true;
      });

      // Fetch activities for full 6-month range
      const dateFrom = new Date(Date.UTC(months[0].year, months[0].month, 1));
      const dateTo = new Date(Date.UTC(months[5].year, months[5].month + 1, 0));

      // Figure out if the selected BU is a "separate" entity (Tirana, Walego) or the main one (Selego).
      // Selego is the main entity — all bank data belongs to it. Only filter EBITDA for other BUs.
      let selectedBuKey = null;
      if (businessUnit._id !== "ALL") {
        try {
          const { data: buData } = await api.get(`/business_unit/${businessUnit._id}`);
          if (buData) selectedBuKey = buData.key;
        } catch (e) { /* ignore */ }
      }
      const isSeparateEntity = selectedBuKey && selectedBuKey !== "SELEGO";
      setIsSeparateBU(isSeparateEntity);

      const [{ data: activities }, ebitdaRes, contractsRes] = await Promise.all([
        api.post("/activity/search", {
          userIds: allMembers.map((u) => u._id),
          dateFrom: dateFrom.getTime(),
          dateTo: dateTo.getTime(),
        }),
        api.get(`/report/ebitda${isSeparateEntity ? `?business_unit_id=${businessUnit}` : ""}`),
        api.post("/user_contract/search", businessUnit._id !== "ALL" ? { business_unit_id: businessUnit._id, limit: 10000 } : { limit: 10000 }),
      ]);

      // Dated compensation periods → resolve each member's cost per forecast month.
      const periodsByUser = groupPeriodsByUser(contractsRes?.data || []);

      // Bank-based overhead data indexed by "year-month"
      const ebitdaByMonth = {};
      (ebitdaRes?.data || []).forEach((e) => {
        ebitdaByMonth[`${e.year}-${e.month}`] = e;
      });

      // Build data per month
      const monthlyData = months.map((m) => {
        const monthStart = new Date(Date.UTC(m.year, m.month, 1));
        const monthEnd = new Date(Date.UTC(m.year, m.month + 1, 0));

        // Activities for this month
        const monthActs = (activities || []).filter((a) => {
          const d = new Date(a.date);
          return d.getFullYear() === m.year && d.getMonth() === m.month;
        });
        const workActs = monthActs.filter((a) => !TIME_OFF_PROJECTS.includes((a.projectName || "").toLowerCase()));

        // Active members for this month
        const activeMembers = allMembers.filter((mb) => {
          if (mb.exit_date && new Date(mb.exit_date) < monthStart) return false;
          if (mb.starting_date && new Date(mb.starting_date) > monthEnd) return false;
          return true;
        });

        // === P&L LINES ===

        // Revenue: only billable activities (exclude admin & invest budget types, same as ProfitabilityCard)
        // Admin/invest work (e.g. prospection, internal tools) = cost only, not revenue
        const NON_REVENUE_TYPES = ["admin", "invest"];
        const revenueActs = workActs.filter((a) => !NON_REVENUE_TYPES.includes(a.budgetType));

        let actualRevenue = 0;
        let forecastRevenue = 0;
        let bestEstimateRevenue = 0;
        revenueActs.forEach((a) => {
          const actual = a.value || 0;
          const predicted = a.predicted_total || 0;
          const tjms = a.userTjms || 0;
          const forecast = (predicted / 8) * tjms;
          actualRevenue += actual;
          forecastRevenue += forecast;
          bestEstimateRevenue += Math.max(actual, forecast);
        });
        // Past = actuals, Current = best estimate (max of actual/forecast per activity), Future = forecast
        const displayRevenue = m.isPast ? actualRevenue : m.isCurrent ? bestEstimateRevenue : forecastRevenue;

        // Costs: fixed salaries + freelancers monthly + variable (daily)
        // Use same fraction logic as ProfitabilityCard: totalDays / 30.44
        let salaryCosts = 0; // employees (CDI, CDD, etc.)
        let freeMonthly = 0; // freelancers paid monthly
        activeMembers.forEach((mb) => {
          if (mb.payment_type !== "MONTHLY") return;
          const monthlyCost = resolveMonthlyCost(periodsByUser, mb, monthEnd) || mb.payment_value || 0;

          let actualStart = monthStart;
          if (mb.starting_date && new Date(mb.starting_date) > monthStart) {
            actualStart = new Date(mb.starting_date);
          }
          let actualEnd = monthEnd;
          if (mb.exit_date && new Date(mb.exit_date) < monthEnd) {
            actualEnd = new Date(mb.exit_date);
          }
          if (actualStart > monthEnd || actualEnd < monthStart) return;

          const totalDays = Math.ceil((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;
          const monthsFraction = totalDays / 30.44;
          const cost = monthlyCost * monthsFraction;
          if (mb.contract === "FREELANCE") {
            freeMonthly += cost;
          } else {
            salaryCosts += cost;
          }
        });
        const fixedCosts = salaryCosts + freeMonthly;

        // Variable costs: use ALL activities (including leave) for daily workers — same as ProfitabilityCard
        // Only exclude "Unavailable"
        const costActs = monthActs.filter((a) => (a.projectName || "").toLowerCase() !== "unavailable");
        let variableCosts = 0;
        costActs.forEach((a) => {
          const member = allMembers.find((mb) => mb._id === a.userId);
          if (!member || member.payment_type !== "DAILY") return;
          const actualCost = a.cost || 0;
          const forecastCost = ((a.predicted_total || 0) / 8) * (a.userTjm || member.tjm || 0);
          if (m.isPast) {
            variableCosts += actualCost;
          } else if (m.isCurrent) {
            variableCosts += Math.max(actualCost, forecastCost);
          } else {
            variableCosts += forecastCost;
          }
        });

        const totalDirectCosts = fixedCosts + variableCosts;

        // === OVERHEAD FROM BANK DATA ===
        const ebitdaMonth = ebitdaByMonth[`${m.year}-${m.month}`] || {};
        // fixedCosts = Selego internal overhead (office, tools, accounting, etc.)
        const overheadFixed = Math.abs(ebitdaMonth.fixedCosts || 0);
        // projectsCosts = client-related expenses (travel, hosting per project)
        const overheadClient = Math.abs(ebitdaMonth.projectsCosts || 0);
        // businessIntroducerCosts = referral/introducer fees
        const overheadBI = Math.abs(ebitdaMonth.businessIntroducerCosts || 0);
        const totalOverhead = overheadFixed + overheadClient + overheadBI;

        const totalCosts = totalDirectCosts + totalOverhead;
        const grossMargin = displayRevenue - totalDirectCosts;
        const marginPct = displayRevenue > 0 ? (grossMargin / displayRevenue) * 100 : totalDirectCosts > 0 ? -100 : 0;
        const ebitda = displayRevenue - totalCosts;
        const ebitdaPct = displayRevenue > 0 ? (ebitda / displayRevenue) * 100 : totalCosts > 0 ? -100 : 0;

        // Forecast vs actual for current month
        const proratedForecast = forecastRevenue * (m.progressPct / 100);
        const revenueDelta = m.isCurrent ? actualRevenue - proratedForecast : 0;
        const projectedRevenue = m.isCurrent && m.progressPct > 10 ? (actualRevenue / m.progressPct) * 100 : forecastRevenue;

        // === PER PERSON ===
        const personData = [];
        activeMembers.forEach((mb) => {
          const memberActs = workActs.filter((a) => a.userId === mb._id);
          const hasActivities = memberActs.length > 0 || mb.payment_type === "MONTHLY";
          if (!hasActivities && mb.status === "inactive") return;

          // Revenue: only billable activities (exclude admin & invest)
          const memberRevActs = memberActs.filter((a) => !NON_REVENUE_TYPES.includes(a.budgetType));
          const memberActualRev = memberRevActs.reduce((s, a) => s + (a.value || 0), 0);
          const memberForecastRev = memberRevActs.reduce((s, a) => {
            const predicted = a.predicted_total || 0;
            const tjms = a.userTjms || 0;
            return s + (predicted / 8) * tjms;
          }, 0);
          const memberBestEstRev = memberRevActs.reduce((s, a) => {
            const actual = a.value || 0;
            const forecast = ((a.predicted_total || 0) / 8) * (a.userTjms || 0);
            return s + Math.max(actual, forecast);
          }, 0);
          // Past: actuals. Current: best estimate (max per activity). Future: forecast.
          const memberRevenue = m.isPast ? memberActualRev : m.isCurrent ? memberBestEstRev : memberForecastRev;

          // All activities for this member (including leave, excluding only "Unavailable") — for cost
          const memberCostActs = costActs.filter((a) => a.userId === mb._id);

          let memberCost = 0;
          if (mb.payment_type === "MONTHLY") {
            // Same fraction logic as ProfitabilityCard: totalDays / 30.44
            let actualStart = monthStart;
            if (mb.starting_date && new Date(mb.starting_date) > monthStart) actualStart = new Date(mb.starting_date);
            let actualEnd = monthEnd;
            if (mb.exit_date && new Date(mb.exit_date) < monthEnd) actualEnd = new Date(mb.exit_date);
            if (actualStart <= monthEnd && actualEnd >= monthStart) {
              const totalDays = Math.ceil((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;
              const monthsFraction = totalDays / 30.44;
              memberCost = (resolveMonthlyCost(periodsByUser, mb, monthEnd) || mb.payment_value || 0) * monthsFraction;
            }
          } else if (m.isPast) {
            memberCost = memberCostActs.reduce((s, a) => s + (a.cost || 0), 0);
          } else if (m.isCurrent) {
            memberCost = memberCostActs.reduce((s, a) => {
              const actualC = a.cost || 0;
              const forecastC = ((a.predicted_total || 0) / 8) * (a.userTjm || mb.tjm || 0);
              return s + Math.max(actualC, forecastC);
            }, 0);
          } else {
            memberCost = memberCostActs.reduce((s, a) => s + ((a.predicted_total || 0) / 8) * (a.userTjm || mb.tjm || 0), 0);
          }

          const memberMargin = memberRevenue - memberCost;
          const memberMarginPct = memberRevenue > 0 ? (memberMargin / memberRevenue) * 100 : memberCost > 0 ? -100 : 0;

          const forecastHours = memberActs.reduce((s, a) => s + (a.predicted_total || 0), 0);
          const actualHours = memberActs.reduce((s, a) => s + (a.total || 0), 0);

          // Projects
          const projectMap = new Map();
          memberActs.forEach((a) => {
            const key = a.projectName || "Unknown";
            if (!projectMap.has(key)) projectMap.set(key, { name: key, hours: 0, forecastHours: 0, revenue: 0, forecastRevenue: 0, budgetType: a.budgetType || "" });
            const p = projectMap.get(key);
            p.hours += a.total || 0;
            p.forecastHours += a.predicted_total || 0;
            p.revenue += a.value || 0;
            p.forecastRevenue += ((a.predicted_total || 0) / 8) * (a.userTjms || 0);
          });

          personData.push({
            userId: mb._id,
            name: mb.name,
            contract: mb.contract,
            paymentType: mb.payment_type,
            companyCountry: mb.company_country,
            position: mb.position,
            office: mb.office,
            tjms: mb.tjms || 0,
            revenue: memberRevenue,
            forecastRevenue: memberForecastRev,
            actualRevenue: memberActualRev,
            cost: memberCost,
            margin: memberMargin,
            marginPct: memberMarginPct,
            forecastHours,
            actualHours,
            projects: Array.from(projectMap.values()).sort((a, b) => (m.isPast ? b.revenue - a.revenue : b.forecastRevenue - a.forecastRevenue)),
          });
        });

        return {
          ...m,
          headcount: activeMembers.length,
          actualRevenue,
          forecastRevenue,
          displayRevenue,
          salaryCosts,
          freeMonthly,
          fixedCosts,
          variableCosts,
          totalDirectCosts,
          overheadFixed,
          overheadClient,
          overheadBI,
          totalOverhead,
          totalCosts,
          grossMargin,
          marginPct,
          ebitda,
          ebitdaPct,
          proratedForecast,
          revenueDelta,
          projectedRevenue,
          personData,
        };
      });

      // For current/future months with no bank data, use rolling 3-month average of past overhead
      const pastWithOverhead = monthlyData.filter((m) => m.isPast && m.totalOverhead > 0);
      if (pastWithOverhead.length > 0) {
        const recent = pastWithOverhead.slice(-3); // last 3 months with data
        const avgFixed = recent.reduce((s, m) => s + m.overheadFixed, 0) / recent.length;
        const avgClient = recent.reduce((s, m) => s + m.overheadClient, 0) / recent.length;
        const avgBI = recent.reduce((s, m) => s + m.overheadBI, 0) / recent.length;

        monthlyData.forEach((m) => {
          if ((m.isCurrent || m.isFuture) && m.totalOverhead === 0) {
            m.overheadFixed = Math.round(avgFixed);
            m.overheadClient = Math.round(avgClient);
            m.overheadBI = Math.round(avgBI);
            m.totalOverhead = m.overheadFixed + m.overheadClient + m.overheadBI;
            m.overheadEstimated = true; // flag for UI
            m.totalCosts = m.totalDirectCosts + m.totalOverhead;
            m.ebitda = m.displayRevenue - m.totalCosts;
            m.ebitdaPct = m.displayRevenue > 0 ? (m.ebitda / m.displayRevenue) * 100 : m.totalCosts > 0 ? -100 : 0;
          }
        });
      }

      // Debug: log P&L breakdown per month for cross-checking with CFO sheet
      console.table(monthlyData.map((m) => ({
        month: m.fullLabel,
        type: m.isPast ? "Real" : m.isCurrent ? "Current" : "Forecast",
        revenue: Math.round(m.displayRevenue),
        salaryCosts: Math.round(m.salaryCosts),
        freeMonthly: Math.round(m.freeMonthly),
        freeDaily: Math.round(m.variableCosts),
        directCosts: Math.round(m.totalDirectCosts),
        overheadFixed: Math.round(m.overheadFixed),
        overheadClient: Math.round(m.overheadClient),
        overheadBI: Math.round(m.overheadBI),
        totalOverhead: Math.round(m.totalOverhead),
        totalCosts: Math.round(m.totalCosts),
        ebitda: Math.round(m.ebitda),
        ebitdaPct: m.ebitdaPct?.toFixed(1) + "%",
        overheadEstimated: m.overheadEstimated ? "⚠ avg" : "bank",
      })));

      setData(monthlyData);
    } catch (error) {
      console.error(error);
      toast.error("Error loading forecast data");
    } finally {
      setLoading(false);
    }
  };

  // Daily freelancers with costs (for named lines in P&L)
  const dailyFreelancers = useMemo(() => {
    if (!data) return [];
    // Collect all daily freelancers who have costs in any month
    const freelancerMap = new Map();
    data.forEach((m) => {
      m.personData.forEach((p) => {
        if (p.contract === "FREELANCE" && p.paymentType === "DAILY" && p.cost > 0) {
          if (!freelancerMap.has(p.userId)) {
            freelancerMap.set(p.userId, { userId: p.userId, name: p.name });
          }
        }
      });
    });
    return Array.from(freelancerMap.values()).sort((a, b) => {
      // Sort by total cost descending
      const totalA = data.reduce((s, m) => s + (m.personData.find((p) => p.userId === a.userId)?.cost || 0), 0);
      const totalB = data.reduce((s, m) => s + (m.personData.find((p) => p.userId === b.userId)?.cost || 0), 0);
      return totalB - totalA;
    });
  }, [data]);

  // Filtered + sorted persons (use current month as reference)
  const filteredPersons = useMemo(() => {
    if (!data) return [];
    const currentMonth = data.find((m) => m.isCurrent) || data[2];
    let persons = currentMonth.personData;

    if (searchFilter) {
      persons = persons.filter((p) => p.name?.toLowerCase().includes(searchFilter.toLowerCase()));
    }

    const sortFns = {
      margin: (a, b) => b.margin - a.margin,
      revenue: (a, b) => b.revenue - a.revenue,
      cost: (a, b) => b.cost - a.cost,
      name: (a, b) => a.name.localeCompare(b.name),
    };
    return [...persons].sort(sortFns[sortKey] || sortFns.margin);
  }, [data, searchFilter, sortKey]);

  const togglePerson = (userId) => {
    setExpandedPersons((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  // CSV export
  const exportToCSV = () => {
    if (!data) return;
    const rows = [];
    // P&L
    rows.push(["", ...data.map((m) => m.fullLabel)]);
    rows.push(["Headcount", ...data.map((m) => m.headcount)]);
    rows.push(["Working Days", ...data.map((m) => m.workingDays)]);
    rows.push(["Revenue", ...data.map((m) => Math.round(m.displayRevenue))]);
    rows.push(["  Actual Revenue", ...data.map((m) => Math.round(m.actualRevenue))]);
    rows.push(["  Forecast Revenue", ...data.map((m) => Math.round(m.forecastRevenue))]);
    rows.push(["Direct Costs", ...data.map((m) => Math.round(m.totalDirectCosts))]);
    rows.push(["  Coûts Salariés", ...data.map((m) => Math.round(m.salaryCosts))]);
    rows.push(["  Free monthly", ...data.map((m) => Math.round(m.freeMonthly))]);
    rows.push(["  Free daily", ...data.map((m) => Math.round(m.variableCosts))]);
    rows.push(["Gross Margin", ...data.map((m) => Math.round(m.grossMargin))]);
    rows.push(["Gross Margin %", ...data.map((m) => m.marginPct.toFixed(1) + "%")]);
    const hasOverhead = data.some((m) => m.totalOverhead > 0);
    if (!isSeparateBU || hasOverhead) {
      rows.push(["Overhead (bank)", ...data.map((m) => Math.round(m.totalOverhead))]);
      rows.push(["  Frais fixes Selego", ...data.map((m) => Math.round(m.overheadFixed))]);
      rows.push(["  Frais clients", ...data.map((m) => Math.round(m.overheadClient))]);
      rows.push(["  Business introducer", ...data.map((m) => Math.round(m.overheadBI))]);
      rows.push(["Total Costs", ...data.map((m) => Math.round(m.totalCosts))]);
      rows.push(["EBITDA", ...data.map((m) => Math.round(m.ebitda))]);
      rows.push(["EBITDA %", ...data.map((m) => m.ebitdaPct.toFixed(1) + "%")]);
    }
    rows.push([]);
    // Per person
    rows.push(["Person", ...data.flatMap((m) => [`${m.shortLabel} Revenue`, `${m.shortLabel} Cost`, `${m.shortLabel} Margin`])]);
    filteredPersons.forEach((person) => {
      const row = [person.name];
      data.forEach((m) => {
        const mp = m.personData.find((p) => p.userId === person.userId);
        row.push(Math.round(mp?.revenue || 0), Math.round(mp?.cost || 0), Math.round(mp?.margin || 0));
      });
      rows.push(row);
    });

    const csvContent = rows.map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `forecast-pnl-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV exported");
  };

  if (loading) return <Loader />;
  if (!data) return <div className="text-center py-8 text-gray-500">No data available.</div>;

  const current = data.find((m) => m.isCurrent) || data[2];
  const pastCount = data.filter((m) => m.isPast).length;
  const futureCount = data.filter((m) => m.isCurrent || m.isFuture).length;

  return (
    <div className="p-4 space-y-6">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <div>
            <span className="font-medium">P&L Forecast + EBITDA</span> — Revenue & direct costs from activities • Overhead from bank data (3-mo avg for future) • Past: actuals • Current: <span className="text-blue-600 font-medium">max(actual, forecast)</span> • Future: forecast
          </div>
          <div className="text-xs text-gray-400 mt-0.5">
            {current.fullLabel}: {current.elapsed}/{current.workingDays} working days ({Math.round(current.progressPct)}% through)
            {current.revenueDelta !== 0 && (
              <span className={`ml-2 ${getDeltaColor(current.revenueDelta)}`}>
                Revenue vs prorated forecast: {current.revenueDelta >= 0 ? "+" : ""}€{fmtFull(current.revenueDelta)}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <button onClick={exportToCSV} className="px-3 py-1.5 text-xs font-medium rounded-md bg-blue-500 text-white hover:bg-blue-600 flex items-center gap-1.5">
            <FaDownload className="text-[10px]" /> Export
          </button>
        </div>
      </div>

      {/* ============================================ */}
      {/* SECTION 1: P&L STATEMENT (months as columns) */}
      {/* ============================================ */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              {/* Source indicator row */}
              <tr>
                <th className="sticky left-0 bg-gray-50 z-10 w-[220px]" />
                {pastCount > 0 && (
                  <th colSpan={pastCount} className="px-2 py-1 text-center">
                    <div className="flex items-center gap-1 justify-center text-[11px] text-gray-500 font-normal">
                      <span className="flex-1 h-px bg-gray-300" />
                      <span className="whitespace-nowrap px-1">← Actuals →</span>
                      <span className="flex-1 h-px bg-gray-300" />
                    </div>
                  </th>
                )}
                {futureCount > 0 && (
                  <th colSpan={futureCount} className="px-2 py-1 text-center">
                    <div className="flex items-center gap-1 justify-center text-[11px] text-blue-500 font-normal">
                      <span className="flex-1 h-px bg-blue-300" />
                      <span className="whitespace-nowrap px-1">← Forecast →</span>
                      <span className="flex-1 h-px bg-blue-300" />
                    </div>
                  </th>
                )}
              </tr>
              {/* Month headers */}
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 sticky left-0 bg-gray-50 z-10 w-[220px]">
                  P&L
                </th>
                {data.map((m, i) => (
                  <th
                    key={m.label}
                    className={`px-4 py-2 text-right text-xs font-medium min-w-[120px] transition-colors ${
                      hoveredCell.col === i
                        ? "bg-yellow-100"
                        : m.isCurrent
                          ? "bg-blue-100 text-blue-800"
                          : m.isFuture
                            ? "bg-purple-50 text-purple-700"
                            : "text-gray-700"
                    }`}>
                    <div className="font-bold text-sm">{m.shortLabel}</div>
                    <div className="text-[10px] opacity-60">{m.year}</div>
                    {m.isCurrent && (
                      <div className="mt-1">
                        <div className="w-full bg-blue-200 rounded-full h-1">
                          <div className="bg-blue-600 h-1 rounded-full" style={{ width: `${Math.min(100, m.progressPct)}%` }} />
                        </div>
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Real or Forecasted indicator */}
              <tr>
                <td className="px-4 py-1 text-[10px] font-medium text-gray-400 italic sticky left-0 bg-white z-10 w-[220px]">REAL OR FORECASTED</td>
                {data.map((m, i) => (
                  <td key={i} className={`px-4 py-1 text-center text-[10px] font-medium ${hoveredCell.col === i ? "bg-yellow-50" : ""}`}>
                    <span className={`inline-block px-2 py-0.5 rounded ${m.isPast ? "bg-green-100 text-green-700" : m.isCurrent ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                      {m.isPast ? "Real" : m.isCurrent ? "Real + Forecast" : "Forecasted"}
                    </span>
                  </td>
                ))}
              </tr>
              {/* Context rows */}
              <PnLInfoRow label="Headcount" values={data.map((m) => m.headcount)} hoveredCol={hoveredCell.col} />
              <PnLInfoRow label="Working Days" values={data.map((m) => m.workingDays)} hoveredCol={hoveredCell.col} />

              {/* Revenue section */}
              <PnLSectionRow label="REVENUE" colSpan={data.length + 1} />
              <PnLRow label="Forecast Revenue" values={data.map((m) => m.displayRevenue)} hoveredCol={hoveredCell.col} isBold />
              {data.some((m) => m.isCurrent) && (
                <>
                  <PnLSubRow
                    label={`Actual to date (${current.elapsed}/${current.workingDays}d)`}
                    values={data.map((m) => m.isCurrent ? m.actualRevenue : null)}
                    hoveredCol={hoveredCell.col}
                    className="text-blue-500"
                  />
                  <PnLSubRow
                    label="vs prorated forecast"
                    values={data.map((m) => m.isCurrent ? m.revenueDelta : null)}
                    hoveredCol={hoveredCell.col}
                    colorFn={(v) => v !== null ? getDeltaColor(v) : "text-gray-300"}
                    prefix="€"
                    showSign
                  />
                  <PnLSubRow
                    label="Projected EOM (extrapolated)"
                    values={data.map((m) => m.isCurrent ? m.projectedRevenue : null)}
                    hoveredCol={hoveredCell.col}
                    className="text-indigo-500"
                  />
                </>
              )}

              {/* Direct costs section */}
              <PnLSectionRow label="DIRECT COSTS (Coûts Salariés + Freelancers)" colSpan={data.length + 1} />
              <PnLRow label="Direct Costs" values={data.map((m) => m.totalDirectCosts)} hoveredCol={hoveredCell.col} isBold isNegative />
              <PnLSubRow label="Coûts Salariés" values={data.map((m) => m.salaryCosts)} hoveredCol={hoveredCell.col} />
              <PnLSubRow label="Free monthly" values={data.map((m) => m.freeMonthly)} hoveredCol={hoveredCell.col} />
              <PnLSubRow label="Free daily" values={data.map((m) => m.variableCosts)} hoveredCol={hoveredCell.col} />
              {/* Named daily freelancers */}
              {dailyFreelancers.map((fl) => (
                <PnLSubRow
                  key={fl.userId}
                  label={`  ↳ ${fl.name}`}
                  values={data.map((m) => {
                    const p = m.personData.find((pd) => pd.userId === fl.userId);
                    return p ? p.cost : 0;
                  })}
                  hoveredCol={hoveredCell.col}
                  className="text-gray-300 text-[11px]"
                />
              ))}

              {/* Gross Margin */}
              <PnLSectionRow label="GROSS MARGIN" colSpan={data.length + 1} />
              <tr className="bg-gray-50 border-t-2 border-gray-300">
                <td className="px-4 py-3 text-sm font-bold text-gray-900 sticky left-0 bg-gray-50 z-10 w-[220px]">Gross Margin</td>
                {data.map((m, i) => (
                  <td
                    key={m.label}
                    className={`px-4 py-3 text-sm text-right font-bold ${getMarginTextColor(m.marginPct)} ${hoveredCell.col === i ? "bg-yellow-50" : ""}`}
                    onMouseEnter={() => setHoveredCell({ row: "margin", col: i })}
                    title={`Margin: €${fmtFull(m.grossMargin)}\nMargin %: ${m.marginPct.toFixed(1)}%`}>
                    €{fmtFull(m.grossMargin)}
                    <div className="text-[10px] font-normal text-gray-400">{m.marginPct.toFixed(1)}%</div>
                  </td>
                ))}
              </tr>

              {/* Overhead costs from bank data — Selego internal costs only */}
              {(() => {
                const hasOverheadData = data.some((m) => m.totalOverhead > 0);
                // For separate BUs (Tirana, Walego): hide overhead if no BU-tagged bank data
                if (isSeparateBU && !hasOverheadData) return null;
                return (
                  <>
                    <PnLSectionRow
                      label="OVERHEAD (from bank)"
                      colSpan={data.length + 1}
                    />
                    {isSeparateBU && !hasOverheadData && (
                      <tr>
                        <td colSpan={data.length + 1} className="px-4 py-1 text-[10px] text-orange-400 italic">
                          No bank transactions tagged with this business unit yet. Tag transactions with a BU to see overhead here.
                        </td>
                      </tr>
                    )}
                    <PnLRow label="Total Overhead" values={data.map((m) => m.totalOverhead)} hoveredCol={hoveredCell.col} isBold isNegative />
                    <PnLSubRow label="Frais fixes Selego (office, tools…)" values={data.map((m) => m.overheadFixed)} hoveredCol={hoveredCell.col} className={data.some((m) => m.overheadEstimated) ? "text-gray-400 italic" : "text-gray-400"} />
                    <PnLSubRow label="Frais clients (travel, hosting…)" values={data.map((m) => m.overheadClient)} hoveredCol={hoveredCell.col} className={data.some((m) => m.overheadEstimated) ? "text-gray-400 italic" : "text-gray-400"} />
                    <PnLSubRow
                      label="Ratio frais clients / CA"
                      values={data.map((m) => m.displayRevenue > 0 ? Math.round((m.overheadClient / m.displayRevenue) * 100) : 0)}
                      hoveredCol={hoveredCell.col}
                      prefix=" "
                      suffix="%"
                      className="text-gray-400 italic text-[11px]"
                    />
                    <PnLSubRow label="Business introducer" values={data.map((m) => m.overheadBI)} hoveredCol={hoveredCell.col} />
                    {data.some((m) => m.overheadEstimated) && (
                      <tr>
                        <td colSpan={data.length + 1} className="px-4 py-1 text-[10px] text-orange-500 italic">
                          ⚠ Future months use 3-month rolling average for overhead (no bank data yet)
                        </td>
                      </tr>
                    )}
                  </>
                );
              })()}

              {/* EBITDA (with overhead) or Gross Margin (no overhead for separate BUs) */}
              {(() => {
                const hasOverhead = data.some((m) => m.totalOverhead > 0);
                const showEbitda = !isSeparateBU || hasOverhead;
                const resultLabel = showEbitda ? "EBITDA" : "GROSS MARGIN";
                return (
                  <>
                    <PnLSectionRow label={showEbitda ? "EBITDA" : "GROSS MARGIN (no overhead data for this BU)"} colSpan={data.length + 1} />
                    <tr className="bg-gray-100 border-t-2 border-gray-400">
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 sticky left-0 bg-gray-100 z-10 w-[220px]">{resultLabel}</td>
                      {data.map((m, i) => {
                        const result = showEbitda ? m.ebitda : m.grossMargin;
                        const revenue = m.displayRevenue;
                        const pct = revenue > 0 ? (result / revenue) * 100 : result < 0 ? -100 : 0;
                        return (
                          <td
                            key={m.label}
                            className={`px-4 py-3 text-sm text-right font-bold ${getDeltaColor(result)} ${hoveredCell.col === i ? "bg-yellow-50" : ""}`}
                            title={`${resultLabel}: €${fmtFull(result)}\n${pct.toFixed(1)}%\nRevenue: €${fmtFull(revenue)}\nDirect costs: €${fmtFull(m.totalDirectCosts)}${showEbitda ? `\nOverhead: €${fmtFull(m.totalOverhead)}` : ""}`}>
                            €{fmtFull(result)}
                            <div className="text-[10px] font-normal">{pct.toFixed(1)}%</div>
                          </td>
                        );
                      })}
                    </tr>
                  </>
                );
              })()}

              {/* Unit economics */}
              {(() => {
                const hasOverhead = data.some((m) => m.totalOverhead > 0);
                const showEbitda = !isSeparateBU || hasOverhead;
                return (
                  <>
                    <PnLSectionRow label="UNIT ECONOMICS" colSpan={data.length + 1} />
                    <PnLSubRow
                      label="Revenue / head"
                      values={data.map((m) => m.headcount > 0 ? Math.round(m.displayRevenue / m.headcount) : 0)}
                      hoveredCol={hoveredCell.col}
                      prefix="€"
                    />
                    <PnLSubRow
                      label="Direct cost / head"
                      values={data.map((m) => m.headcount > 0 ? Math.round(m.totalDirectCosts / m.headcount) : 0)}
                      hoveredCol={hoveredCell.col}
                      prefix="€"
                    />
                    <PnLSubRow
                      label={`${showEbitda ? "EBITDA" : "Margin"} / head`}
                      values={data.map((m) => {
                        const result = showEbitda ? m.ebitda : m.grossMargin;
                        return m.headcount > 0 ? Math.round(result / m.headcount) : 0;
                      })}
                      hoveredCol={hoveredCell.col}
                      prefix="€"
                      colorFn={(v) => getDeltaColor(v)}
                    />
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===================================================== */}
      {/* SECTION 2: TEAM CONTRIBUTION MATRIX (SalesOps-style)  */}
      {/* ===================================================== */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-fixed">
            <thead className="bg-gray-50">
              {/* Source indicator */}
              <tr>
                <th className="sticky left-0 bg-gray-50 z-10 w-[200px]" />
                {pastCount > 0 && (
                  <th colSpan={pastCount * 3} className="px-2 py-1 text-center">
                    <div className="flex items-center gap-1 justify-center text-[11px] text-gray-500 font-normal">
                      <span className="flex-1 h-px bg-gray-300" />
                      <span className="whitespace-nowrap px-1">← Actuals →</span>
                      <span className="flex-1 h-px bg-gray-300" />
                    </div>
                  </th>
                )}
                {futureCount > 0 && (
                  <th colSpan={futureCount * 3} className="px-2 py-1 text-center">
                    <div className="flex items-center gap-1 justify-center text-[11px] text-blue-500 font-normal">
                      <span className="flex-1 h-px bg-blue-300" />
                      <span className="whitespace-nowrap px-1">← Forecast →</span>
                      <span className="flex-1 h-px bg-blue-300" />
                    </div>
                  </th>
                )}
              </tr>
              {/* Month group headers */}
              <tr>
                <th className="sticky left-0 bg-gray-50 z-10 w-[200px]">
                  <div className="px-3 py-1 flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchFilter}
                      onChange={(e) => setSearchFilter(e.target.value)}
                      className="w-28 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                    <select
                      value={sortKey}
                      onChange={(e) => setSortKey(e.target.value)}
                      className="px-1.5 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500">
                      <option value="margin">By margin</option>
                      <option value="revenue">By revenue</option>
                      <option value="cost">By cost</option>
                      <option value="name">By name</option>
                    </select>
                  </div>
                </th>
                {data.map((m) => (
                  <th
                    key={m.label}
                    colSpan={3}
                    className={`px-2 py-2 text-center text-xs font-bold border-l ${
                      m.isCurrent ? "bg-blue-100 text-blue-800 border-blue-300" : m.isFuture ? "bg-purple-50 text-purple-700 border-purple-200" : "text-gray-700 border-gray-200"
                    }`}>
                    {m.shortLabel} {m.year}
                  </th>
                ))}
              </tr>
              {/* Sub-column headers */}
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 sticky left-0 bg-gray-50 z-10 w-[200px]">
                  Person
                  <span className="text-[10px] text-gray-400 ml-2">{filteredPersons.length} people</span>
                </th>
                {data.map((m) => (
                  <React.Fragment key={`sub-${m.label}`}>
                    <th className={`px-2 py-2 text-right text-[10px] font-medium min-w-[80px] ${m.isCurrent ? "bg-blue-50 text-blue-600" : m.isFuture ? "bg-purple-50/50 text-purple-500" : "text-gray-500"}`}>
                      Revenue
                    </th>
                    <th className={`px-2 py-2 text-right text-[10px] font-medium min-w-[70px] ${m.isCurrent ? "bg-blue-50 text-blue-600" : m.isFuture ? "bg-purple-50/50 text-purple-500" : "text-gray-500"}`}>
                      Cost
                    </th>
                    <th className={`px-2 py-2 text-center text-[10px] font-medium min-w-[80px] border-r ${m.isCurrent ? "bg-blue-50 text-blue-600 border-blue-200" : m.isFuture ? "bg-purple-50/50 text-purple-500 border-purple-200" : "text-gray-500 border-gray-200"}`}>
                      Margin
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody onMouseLeave={() => setHoveredCell({ row: null, col: null })}>
              {/* TOTAL ROW */}
              <tr className="bg-gray-100 border-b-2 border-gray-300 font-semibold">
                <td className="px-4 py-2 text-sm font-bold text-gray-700 sticky left-0 bg-gray-100 z-10 w-[200px]">TOTAL</td>
                {data.map((m) => (
                  <React.Fragment key={`total-${m.label}`}>
                    <td className="px-2 py-2 text-right text-sm font-bold text-gray-900">€{fmt(m.displayRevenue)}</td>
                    <td className="px-2 py-2 text-right text-sm font-bold text-gray-900">€{fmt(m.totalCosts)}</td>
                    <td className={`px-2 py-2 text-center border-r border-gray-300`}>
                      <span className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${getMarginColor(m.marginPct)}`}>
                        {m.marginPct.toFixed(0)}%
                      </span>
                    </td>
                  </React.Fragment>
                ))}
              </tr>

              {/* PERSON ROWS */}
              {filteredPersons.map((person, rowIndex) => {
                const isExpanded = expandedPersons.has(person.userId);
                return (
                  <React.Fragment key={person.userId}>
                    <tr
                      className={`border-b transition-colors ${hoveredCell.row === rowIndex ? "bg-yellow-50" : "hover:bg-gray-50"}`}
                      onMouseEnter={() => setHoveredCell({ row: rowIndex, col: hoveredCell.col })}>
                      <td
                        className={`px-3 py-2 sticky left-0 z-10 w-[200px] transition-colors ${hoveredCell.row === rowIndex ? "bg-yellow-100" : "bg-white"}`}>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => togglePerson(person.userId)} className="text-gray-400 hover:text-gray-600 text-[10px] w-3">
                            {isExpanded ? "▼" : "▶"}
                          </button>
                          <div className="cursor-pointer" onClick={() => openUserActivity(person.userId)}>
                            <div className="text-sm font-medium text-gray-900 truncate hover:text-blue-600">{person.name}</div>
                            <div className="text-[10px] text-gray-400 truncate">
                              {[person.contract, person.position, person.office].filter(Boolean).join(" • ")}
                              {person.tjms > 0 && <span className="ml-1">• TJM {person.tjms}€</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      {data.map((m, colIndex) => {
                        const mp = m.personData.find((p) => p.userId === person.userId);
                        const isHovered = hoveredCell.row === rowIndex || hoveredCell.col === colIndex;
                        if (!mp) {
                          return (
                            <React.Fragment key={`${person.userId}-${m.label}`}>
                              <td className={`px-2 py-2 text-right text-xs text-gray-300 ${isHovered ? "bg-yellow-50/50" : ""}`}>—</td>
                              <td className={`px-2 py-2 text-right text-xs text-gray-300 ${isHovered ? "bg-yellow-50/50" : ""}`}>—</td>
                              <td className={`px-2 py-2 text-center border-r text-xs text-gray-300 ${m.isCurrent ? "border-blue-200" : m.isFuture ? "border-purple-200" : "border-gray-200"} ${isHovered ? "bg-yellow-50/50" : ""}`}>—</td>
                            </React.Fragment>
                          );
                        }

                        const revTitle = m.isCurrent
                          ? `Revenue: €${fmtFull(mp.revenue)}\nForecast: €${fmtFull(mp.forecastRevenue)}\nActual: €${fmtFull(mp.actualRevenue)}\nHours: ${mp.actualHours.toFixed(1)} actual / ${mp.forecastHours.toFixed(1)} forecast`
                          : m.isFuture
                            ? `Forecast Revenue: €${fmtFull(mp.forecastRevenue)}\nForecast Hours: ${mp.forecastHours.toFixed(1)}`
                            : `Revenue: €${fmtFull(mp.revenue)}\nHours: ${mp.actualHours.toFixed(1)}`;

                        return (
                          <React.Fragment key={`${person.userId}-${m.label}`}>
                            <td
                              className={`px-2 py-2 text-right text-xs text-gray-700 ${isHovered ? "bg-yellow-50/50" : ""}`}
                              onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                              title={revTitle}>
                              {mp.revenue > 0 ? `€${fmt(mp.revenue)}` : <span className="text-gray-300">0</span>}
                            </td>
                            <td
                              className={`px-2 py-2 text-right text-xs text-gray-700 ${isHovered ? "bg-yellow-50/50" : ""}`}
                              onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}
                              title={`Cost: €${fmtFull(mp.cost)}\nType: ${mp.paymentType}`}>
                              {mp.cost > 0 ? `€${fmt(mp.cost)}` : <span className="text-gray-300">0</span>}
                            </td>
                            <td
                              className={`px-2 py-2 text-center border-r ${m.isCurrent ? "border-blue-200" : m.isFuture ? "border-purple-200" : "border-gray-200"}`}
                              onMouseEnter={() => setHoveredCell({ row: rowIndex, col: colIndex })}>
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${getMarginColor(mp.marginPct)}`}
                                title={`Margin: €${fmtFull(mp.margin)} (${mp.marginPct.toFixed(1)}%)`}>
                                {mp.marginPct.toFixed(0)}%
                              </span>
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>

                    {/* Expanded: project rows */}
                    {isExpanded && person.projects.map((proj) => (
                      <tr key={`${person.userId}-${proj.name}`} className="bg-gray-50/70">
                        <td className="pl-10 pr-3 py-1.5 text-[11px] text-gray-500 sticky left-0 bg-gray-50/70 z-10 w-[200px] truncate" title={proj.name}>
                          {proj.budgetType && <span className="text-[9px] uppercase text-gray-400 mr-1">[{proj.budgetType}]</span>}
                          {proj.name}
                        </td>
                        {data.map((m) => {
                          const mp = m.personData.find((p) => p.userId === person.userId);
                          const mProj = mp?.projects.find((p) => p.name === proj.name);
                          if (!mProj) return (
                            <React.Fragment key={`${person.userId}-${proj.name}-${m.label}`}>
                              <td className="px-2 py-1 text-right text-[10px] text-gray-300">—</td>
                              <td className="px-2 py-1 text-right text-[10px] text-gray-300" />
                              <td className={`px-2 py-1 border-r text-[10px] text-gray-300 ${m.isCurrent ? "border-blue-200" : m.isFuture ? "border-purple-200" : "border-gray-200"}`} />
                            </React.Fragment>
                          );
                          const rev = m.isPast ? mProj.revenue : mProj.forecastRevenue;
                          const hours = m.isPast ? mProj.hours : mProj.forecastHours;
                          return (
                            <React.Fragment key={`${person.userId}-${proj.name}-${m.label}`}>
                              <td className="px-2 py-1 text-right text-[10px] text-gray-500" title={`${hours.toFixed(1)}h`}>
                                {rev > 0 ? `€${fmt(rev)}` : <span className="text-gray-300">0</span>}
                              </td>
                              <td className="px-2 py-1 text-right text-[10px] text-gray-400">
                                {hours > 0 ? `${(hours / 8).toFixed(1)}d` : ""}
                              </td>
                              <td className={`px-2 py-1 border-r text-[10px] ${m.isCurrent ? "border-blue-200" : m.isFuture ? "border-purple-200" : "border-gray-200"}`} />
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {filteredPersons.length === 0 && <div className="text-center py-8 text-gray-500">No team members found.</div>}
    </div>
  );
}

// ============================================
// P&L Table Sub-components
// ============================================

function PnLSectionRow({ label, colSpan }) {
  return (
    <tr className="bg-gray-100 border-t-2 border-gray-300">
      <td colSpan={colSpan} className="px-4 py-1.5 text-[11px] font-bold text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-100 z-10">
        {label}
      </td>
    </tr>
  );
}

function PnLRow({ label, values, hoveredCol, isBold, isNegative }) {
  return (
    <tr className={isBold ? "bg-gray-50/50" : ""}>
      <td className={`px-4 py-2 text-sm sticky left-0 z-10 w-[220px] ${isBold ? "font-semibold text-gray-900 bg-gray-50/50" : "text-gray-700 bg-white"}`}>{label}</td>
      {values.map((v, i) => (
        <td
          key={i}
          className={`px-4 py-2 text-sm text-right ${isBold ? "font-semibold" : ""} ${isNegative ? "text-red-700" : "text-gray-900"} ${hoveredCol === i ? "bg-yellow-50" : ""}`}>
          €{fmtFull(v)}
        </td>
      ))}
    </tr>
  );
}

function PnLSubRow({ label, values, hoveredCol, className = "text-gray-400", prefix = "", suffix = "", colorFn, showSign = false }) {
  return (
    <tr>
      <td className="px-4 py-1.5 text-xs text-gray-400 pl-8 sticky left-0 bg-white z-10 w-[220px]">{label}</td>
      {values.map((v, i) => {
        const display =
          v !== null && v !== undefined
            ? `${showSign && v > 0 ? "+" : ""}${prefix !== "" ? prefix : "€"}${fmtFull(v)}${suffix}`
            : "";
        return (
          <td key={i} className={`px-4 py-1.5 text-xs text-right ${colorFn ? colorFn(v) : className} ${hoveredCol === i ? "bg-yellow-50" : ""}`}>
            {display}
          </td>
        );
      })}
    </tr>
  );
}

function PnLInfoRow({ label, values, hoveredCol }) {
  return (
    <tr>
      <td className="px-4 py-1 text-xs text-gray-400 italic sticky left-0 bg-white z-10 w-[220px]">{label}</td>
      {values.map((v, i) => (
        <td key={i} className={`px-4 py-1 text-xs text-right text-gray-400 italic ${hoveredCol === i ? "bg-yellow-50" : ""}`}>
          {v}
        </td>
      ))}
    </tr>
  );
}
